/**
 * Credit-hours sentinel logic (frontend).
 *
 * Mirrors the backend helper at
 * lambda_layers/common-queries/nodejs/credit-hours.mjs. Kept as a separate
 * copy because the browser bundle cannot import from the Lambda layer
 * (/opt/nodejs/...).
 *
 * Credit-hours fields (hours, maxHours, proposedHours) distinguish three
 * states a plain number/null cannot:
 *   - null / undefined / '' : "not set"
 *   - 0.0001                : an explicit, user-entered zero ("true zero")
 *   - any other number      : a real credit-hours value
 *
 * A genuine 0 is ENCODED to the sentinel (0.0001) on save so it survives a
 * round-trip without being confused with "not set".
 */

export const CREDIT_HOURS_SENTINEL = 0.0001;

/**
 * Encode a user-entered credit-hours value into its stored form.
 * - a genuine 0 (or "0")        -> 0.0001 (sentinel, "true zero")
 * - any other number / numeric string -> that number
 * - '' / null / undefined / non-numeric -> null ("not set")
 *
 * @param {number|string|null|undefined} value
 * @returns {number|null}
 */
export function encodeHours(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    // Non-numeric input is never a valid credit-hours value — store null.
    return null;
  }
  return num === 0 ? CREDIT_HOURS_SENTINEL : num;
}

/**
 * Decode a stored credit-hours value into a display/edit value.
 * - 0.0001 (sentinel) -> 0 (true zero)
 * - plain 0           -> null when { zeroAsNull: true }, otherwise 0
 * - null / undefined  -> null
 * - any other number  -> unchanged
 *
 * @param {number|null|undefined} value
 * @param {Object} [options]
 * @param {boolean} [options.zeroAsNull=false]
 *   When true, a plain stored 0 (legacy "not set") decodes to null.
 * @returns {number|null}
 */
export function decodeHours(value, options = {}) {
  const { zeroAsNull = false } = options;

  if (value === CREDIT_HOURS_SENTINEL) return 0;
  if (value === 0) return zeroAsNull ? null : 0;
  if (value === null || value === undefined) return null;
  return value;
}

/**
 * Return a copy of a course with its credit-hours fields encoded for storage.
 * Encodes hours, maxHours, and proposedHours (only keys the course already
 * has) and recurses into nested recommended courses. Non-hour fields are
 * preserved untouched.
 *
 * @param {Object} course
 * @returns {Object} a new course object with encoded hour fields
 */
export function encodeCourseHours(course) {
  if (!course) return course;
  const encoded = { ...course };
  if ('hours' in encoded) encoded.hours = encodeHours(encoded.hours);
  if ('maxHours' in encoded) encoded.maxHours = encodeHours(encoded.maxHours);
  if ('proposedHours' in encoded)
    encoded.proposedHours = encodeHours(encoded.proposedHours);
  if (Array.isArray(encoded.recommended?.courses)) {
    encoded.recommended = {
      ...encoded.recommended,
      courses: encoded.recommended.courses.map(encodeCourseHours),
    };
  }
  return encoded;
}

/**
 * Return a copy of a course with its credit-hours fields decoded for
 * editing/display. Decodes hours, maxHours, and proposedHours (only keys the
 * course already has) and recurses into nested recommended courses.
 *
 * @param {Object} course
 * @param {Object} [options] - same options as decodeHours (e.g. { zeroAsNull: true })
 * @returns {Object} a new course object with decoded hour fields
 */
export function decodeCourseHours(course, options = {}) {
  if (!course) return course;
  const decoded = { ...course };
  if ('hours' in decoded) decoded.hours = decodeHours(decoded.hours, options);
  if ('maxHours' in decoded)
    decoded.maxHours = decodeHours(decoded.maxHours, options);
  if ('proposedHours' in decoded)
    decoded.proposedHours = decodeHours(decoded.proposedHours, options);
  if (Array.isArray(decoded.recommended?.courses)) {
    decoded.recommended = {
      ...decoded.recommended,
      courses: decoded.recommended.courses.map((c) =>
        decodeCourseHours(c, options)
      ),
    };
  }
  return decoded;
}
